import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { APP_ROUTES } from "../../../constants/appRoutes";

export function ProgressMessage({ message, status }) {
  return (
    <motion.li
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="flex items-center gap-3 rounded-lg border border-slate-100 bg-white px-4 py-3 shadow-sm"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center">
        {status === "done" ? (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <i className="bi bi-check-lg text-lg" aria-hidden />
          </span>
        ) : status === "active" ? (
          <span
            className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900"
            role="status"
            aria-label="In progress"
          />
        ) : null}
      </span>
      <span className="text-sm font-medium text-slate-800 md:text-base">{message}</span>
    </motion.li>
  );
}

export function ProfileSetupProgressList({ steps, isLoading, hasActiveSteps }) {
  if (steps.length > 0) {
    return (
      <ul className="flex flex-col gap-2.5">
        {steps.map((step) => (
          <ProgressMessage key={step.key} message={step.label} status={step.status} />
        ))}
      </ul>
    );
  }

  if (isLoading && !hasActiveSteps) {
    return (
      <div className="flex justify-center py-6">
        <span
          className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  return null;
}

export function ProfileSetupContinueLater() {
  return (
    <div className="mt-8 flex items-center justify-center gap-2 border-t border-slate-100 pt-6 text-center">
      <i className="bi bi-x-circle text-base text-slate-500" aria-hidden />
      <p className="mb-0 text-sm leading-relaxed text-slate-600">
        Setup is in progress. You can close this.
      </p>
    </div>
  );
}

export function ProfileSetupCompleteActions({
  opportunityCount,
  compact = false,
  onExplore = null,
  onCreateCampaign = null,
}) {
  const exploreClassName =
    "btn btn-dark-outline-app flex w-full items-center justify-center gap-2 no-underline";
  const campaignClassName =
    "btn btn-dark-app flex w-full items-center justify-center gap-2 no-underline";

  return (
    <div className={compact ? "flex flex-col gap-3" : "grid grid-cols-1 gap-3 sm:grid-cols-2"}>
      {onExplore ? (
        <button type="button" className={exploreClassName} onClick={onExplore}>
          <i className="bi bi-stars" aria-hidden />
          Explore Opportunities
        </button>
      ) : (
        <Link to={APP_ROUTES.CONTENT_AI} className={exploreClassName}>
          <i className="bi bi-stars" aria-hidden />
          Explore Opportunities
        </Link>
      )}
      {!compact ? (
        onCreateCampaign ? (
          <button type="button" className={campaignClassName} onClick={onCreateCampaign}>
            <i className="bi bi-megaphone" aria-hidden />
            Create Campaign
          </button>
        ) : (
          <Link to={APP_ROUTES.CAMPAIGN_WIZARD} className={campaignClassName}>
            <i className="bi bi-megaphone" aria-hidden />
            Create Campaign
          </Link>
        )
      ) : null}
    </div>
  );
}

export function ProfileSetupCompleteSummary({ opportunityCount }) {
  return (
    <div className="mb-6 flex flex-col items-center text-center">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full border border-emerald-900 bg-emerald-50 shadow-sm">
        <i className="bi bi-check-lg text-6xl text-emerald-600" aria-hidden />
      </div>
      <p className="mb-2 text-2xl font-bold text-slate-900 md:text-3xl">
        {opportunityCount ?? 0} trends created
      </p>
      <p className="mb-0 max-w-sm text-sm leading-relaxed text-slate-600 md:text-base">
        Your profile is ready. Explore what we found or start your first campaign.
      </p>
    </div>
  );
}
